import { SettingsService } from 'src/app/services/settings/settings.service';
import { Injectable } from '@angular/core';
import { CurrentUiStoreService } from '../ui/current-ui-store.service';
import { AsgardFeatureNames } from 'src/models/AsgardFeatures';
import { WordPredictionApiService, IWordPredictionResponse } from './word-prediction-api.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { HtmlTextWalker, ITxtmapItem } from 'src/app/core/html-and-text-utils';
import { ReadingAtom } from 'src/app/core/reading-utils/interfaces';
import { ReadingUnitType } from 'src/app/core/reading-utils/enums';
import { CurrentUIState } from 'src/models/UIState';
import { environment } from 'src/environments/environment';
import { TabService } from '../tab.service';
import { DocumentManagerService } from '../document/document-manager.service';
import { AsgardDocHtml } from 'src/app/core/documents/asgard-doc-html';
import { AsgardDocPdf } from 'src/app/core/documents/asgard-doc-pdf';
import { isSymbolSpaceOrEnter } from 'src/app/core/common';
import { ISettings } from 'src/app/core/interfaces';
import { DeviceDetectorService } from '../device-detector.service';
import { DeviceTypes } from 'src/models/DeviceTypes';

@Injectable({
    providedIn: 'root'
})
export class WordPredictionService {
    private active: boolean = false;
    private isLocked: boolean = false;
    private isCapitalized: boolean = false;
    private searchingText: string | null = null;
    private previousWordPredictionOnSetting: boolean | null = null;
    private isCursorOnWord: boolean = false;

    private predicts: BehaviorSubject<IWordPredictionResponse | null> = new BehaviorSubject<IWordPredictionResponse | null>(null);

    private get currentDoc(): AsgardDocHtml | null {
        return this.docMgrService.currentDoc instanceof AsgardDocPdf ? null : this.docMgrService.currentDoc as AsgardDocHtml;
    }

    get wordItemUnderCursor(): ReadingAtom | null {
        const wordItem = this.currentDoc !== null ? this.currentDoc.getItemUnderCursor(ReadingUnitType.Word) : null;
        return wordItem && wordItem.length === 1 ? wordItem[0] : null;
    }

    get wordItemBeforeCursor(): ReadingAtom | null {
        const wordItem = this.currentDoc !== null ? this.currentDoc.getItemBeforeCursor(ReadingUnitType.Word) : null;
        return wordItem && wordItem.length === 1 ? wordItem[0] : null;
    }

    private get currentLanguageCode(): string | null {
        return this.tabService.activeTab !== null ? this.tabService.getTabLanguage(this.tabService.activeTab.id) : null;
    }

    get documentIFrame(): HTMLIFrameElement | null {
        if (this.currentDoc) {
            return this.currentDoc.editorFrame;
        }
        return null;
    }

    get buildWalker(): HtmlTextWalker | null {
        if (this.currentDoc && this.currentDoc.editorElement) {
            return new HtmlTextWalker(this.currentDoc.editorElement, document);
        }
        return null;
    }

    get buildTextMap(): ITxtmapItem[] | null {
        if (this.buildWalker) {
            return this.buildWalker.textMap;
        }
        return null;
    }
    constructor(
        private readonly uiStore: CurrentUiStoreService,
        private readonly wordPredictionApiService: WordPredictionApiService,
        private readonly tabService: TabService,
        private readonly docMgrService: DocumentManagerService,
        private readonly settingsService: SettingsService,
        private readonly deviceDetectorService: DeviceDetectorService
    ) {
        this.initSubscirptions();
    }

    onKeyUpHandler = (e: KeyboardEvent) => {
        if (this.active && this.currentDoc !== null && !this.currentDoc.isStaticContent) {
            if (e) {
                this.isCursorOnWord = false;

                if (isSymbolSpaceOrEnter(e.code)) {

                    this.hideComponent();
                    this.predicts.next(null);
                    return;
                }

                const wordItem = this.wordItemBeforeCursor;

                if (wordItem) {
                    this.isCapitalized = this.isWordCapitalized(wordItem.text);
                    this.callApi(wordItem.text.toLowerCase());
                } else {
                    this.hideComponent();
                    this.predicts.next(null);
                }
            }
        }
    }

    onWordClickedHandler = () => {
        if (this.active && this.currentDoc !== null && !this.currentDoc.isStaticContent) {
            const wordItem = this.wordItemUnderCursor;
            this.isCursorOnWord = true;

            if (wordItem) {
                this.isCapitalized = this.isWordCapitalized(wordItem.text);
                this.callApi(wordItem.text.toLowerCase());
            } else {
                this.hideComponent();
                this.predicts.next(null);
            }
        }
    }

    changeToWord(word: string | undefined): void {
        const item = this.isCursorOnWord ? this.wordItemUnderCursor : this.wordItemBeforeCursor;

        if (!item) {
            this.hideComponent();
            return;
        }

        const txtMapItem = this.getWordFromMapByIndex(item.itemIdx);
        if (!this.buildWalker || !txtMapItem) {
            return;
        }
        this.buildWalker.markMisspeledWord(txtMapItem.itemIndex, true);

        if (this.isCapitalized && word) {
            word = this.capitalizeWord(word);
        }

        const node = this.buildWalker.getPredictedWordWrapper(txtMapItem.itemIndex) as HTMLElement;
        if (word) {
            node.innerText = word;
            if (this.currentDoc) {
                this.currentDoc.onDocumentChanged();
                this.unMarkWord(txtMapItem);
                this.hideComponent();
            }
        }
    }

    getPredictObj(): Observable<IWordPredictionResponse | null> {
        return this.predicts.asObservable();
    }

    toggleWordPredictor = () => {
        if (this.active) {
            this.uiStore.setItemUnActive(AsgardFeatureNames.WORD_PREDICTION);
        } else {
            this.uiStore.setItemActive(AsgardFeatureNames.WORD_PREDICTION);
        }
    }

    private isWordCapitalized(word: string): boolean {
        if (word && word.length > 0) {
            const firstLetter = word[0] === word[0].toUpperCase();
            return firstLetter;
        }
        return false;
    }

    private unMarkWord(word: ITxtmapItem): void {
        const incorrectWord = this.getWordFromMapByIndex(word.itemIndex);
        if (incorrectWord && this.buildWalker) {
            const nodeMisspeledWord = this.buildWalker.getPredictedWordWrapper(incorrectWord.itemIndex);
            if (nodeMisspeledWord) {
                const parent = nodeMisspeledWord.parentNode;
                if (parent) {
                    if (nodeMisspeledWord.firstChild) {
                        parent.insertBefore(nodeMisspeledWord.firstChild, nodeMisspeledWord);
                        parent.removeChild(nodeMisspeledWord);
                        this.setCursorToLastPosition(parent as Node);
                    }
                }
            }
        }
    }

    private isResultNotEmpty(result: IWordPredictionResponse): boolean {
        return result && (result.beginSuggestions && result.beginSuggestions.length > 0 ||
            result.generatedSuggestions && result.generatedSuggestions.length > 0);
    }

    private async callApi(word: string): Promise<void> {
        if (this.searchingText === word) {
            return;
        }

        try {
            const langCode = this.currentLanguageCode;

            if (!(environment.wordPrediction.supportedLangCodes.find(i => i === langCode))) {
                return;
            }

            this.searchingText = word;
            if (langCode) {
                const result = await this.wordPredictionApiService.suggestedWords(word, langCode);

                if (result) {
                    this.predicts.next(result);

                    if (this.isResultNotEmpty(result)) {
                        this.showComponent();
                    } else {
                        this.hideComponent();
                    }
                }
            }
        } catch (err) {
            console.log(err);
            this.hideComponent();
        } finally {
            this.searchingText = null;
        }
    }

    private showComponent = () => {
        if (this.active) {
            const feature = this.deviceDetectorService.deviceType === DeviceTypes.DESKTOP
            ? AsgardFeatureNames.WORD_PREDICTION_DIALOG
            : AsgardFeatureNames.WORD_PREDICTION_SPLIT_AREA;
            this.uiStore.setItemActive(feature);
        }
    }

    public hideComponent = () => {
        this.uiStore.setItemUnActive(AsgardFeatureNames.WORD_PREDICTION_DIALOG);
        this.uiStore.setItemUnActive(AsgardFeatureNames.WORD_PREDICTION_SPLIT_AREA);
    }

    private initSubscirptions(): void {
        this.uiStore.getUIState().subscribe(
            (i: CurrentUIState) => {
                this.active = i[AsgardFeatureNames.WORD_PREDICTION].active;
                this.isLocked = i[AsgardFeatureNames.WORD_PREDICTION].locked;
            }
        );

        this.settingsService.getSettingsObservable().subscribe((s: ISettings) => {
            if (s) {
                if (s.wordPredictionOn === this.previousWordPredictionOnSetting) {
                    return;
                }

                this.previousWordPredictionOnSetting = s.wordPredictionOn;
                if (!this.isLocked) {
                    if (s.wordPredictionOn) {
                        this.uiStore.setItemActive(AsgardFeatureNames.WORD_PREDICTION);
                    } else {
                        this.uiStore.setItemUnActive(AsgardFeatureNames.WORD_PREDICTION);
                    }
                }
            }
        });
    }

    private capitalizeWord(word: string): string | undefined {
        if (!word) {
            return;
        }

        return word.charAt(0).toUpperCase() + word.slice(1);
    }

    private getWordFromMapByIndex(idx: number): ITxtmapItem | undefined {
        if (this.buildTextMap) {
            return this.buildTextMap.find(i => i.itemIndex === idx);
        }
    }

    private setCursorToLastPosition(node: Node): void {
        if (this.currentDoc) {
            const element = this.currentDoc.editorElement;
            if (!element) { return; }
            element.focus();

            const win = this.currentDoc.editorWin;
            if (!win) { return; }

            const sel = win.getSelection();
            if (!sel) { return; }

            const range = document.createRange();
            range.selectNodeContents(node);
            range.collapse(false);

            sel.removeAllRanges();
            sel.addRange(range);
        }
    }
}
